import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AppContext } from '../../App';
import licenseService from '../../services/licenseService';
import ExpiringGrantsBanner from './ExpiringGrantsBanner';
import GrantIssuanceForm from './GrantIssuanceForm';
import GrantHistory from './GrantHistory';
import ApplianceList from './ApplianceList';
import { AlertTriangle, Key, Server, Users, RefreshCw } from 'lucide-react';

export default function OfflineLicensingDashboard() {
  const { selectedTeam } = useContext(AppContext);
  const [activeTab, setActiveTab] = useState('grants');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [expiringGrants, setExpiringGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLoading, setShowLoading] = useState(false); // Delayed loading indicator
  const [error, setError] = useState(null);

  // Only show loading indicator after 150ms delay to prevent flash
  useEffect(() => {
    let timer;
    if (loading) {
      timer = setTimeout(() => setShowLoading(true), 150);
    } else {
      setShowLoading(false);
    }
    return () => clearTimeout(timer);
  }, [loading]);

  const loadCustomers = useCallback(async () => {
    try {
      const response = await licenseService.listCustomers();
      if (response.success) {
        setCustomers(response.customers);
        // Select first customer with offline grants by default, or just first customer
        const offlineCustomer = response.customers.find(c => c.has_offline_grants);
        if (!selectedCustomer && response.customers.length > 0) {
          setSelectedCustomer(offlineCustomer || response.customers[0]);
        }
      }
    } catch (err) {
      console.error('Failed to load customers:', err);
      setError(err.message);
    }
  }, [selectedCustomer]);

  const loadExpiringGrants = useCallback(async () => {
    try {
      const response = await licenseService.getExpiringGrants(60, selectedCustomer?.customer_proxy_team_id || null);
      if (response.success) {
        setExpiringGrants(response.grants);
      }
    } catch (err) {
      console.error('Failed to load expiring grants:', err);
    }
  }, [selectedCustomer?.customer_proxy_team_id]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([loadCustomers(), loadExpiringGrants()]);
      setLoading(false);
    };
    loadData();
  }, [loadCustomers, loadExpiringGrants]);

  const handleCustomerChange = (e) => {
    const customerId = e.target.value;
    const customer = customers.find(c => c.customer_proxy_team_id === customerId);
    setSelectedCustomer(customer || null);
  };

  const handleDismissGrant = async (grantId) => {
    try {
      await licenseService.dismissExpiryBanner(grantId);
      setExpiringGrants(prev => prev.filter(g => g.id !== grantId));
    } catch (err) {
      console.error('Failed to dismiss grant:', err);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([loadCustomers(), loadExpiringGrants()]);
    setLoading(false);
  };

  // Helper to render content area based on state. Offline licensing is open
  // to any member of the vendor team — the server enforces team-membership,
  // matching the bar used for API-key issuance.
  const renderContent = () => {
    if (loading) {
      // Only show spinner after delay to prevent flash on fast loads
      if (!showLoading) {
        return (
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-12">
            <div className="h-6" /> {/* Placeholder to maintain height */}
          </div>
        );
      }
      return (
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-12">
          <div className="flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-3 text-slate-400">Loading licensing data...</span>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-950/30 border border-red-800/50 rounded-lg p-6 text-center">
          <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold mb-2 text-slate-100">Error Loading Data</h2>
          <p className="text-slate-400 text-sm mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg flex items-center mx-auto text-sm transition-colors"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Retry
          </button>
        </div>
      );
    }

    return (
      <>
        {expiringGrants.length > 0 && (
          <ExpiringGrantsBanner
            grants={expiringGrants}
            onDismiss={handleDismissGrant}
          />
        )}

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6">
          <div className="flex flex-wrap gap-4 items-end mb-6">
            <div className="flex-1 min-w-64">
              <label className="block text-sm font-medium mb-2 text-slate-300">Select Customer</label>
              <select
                value={selectedCustomer?.customer_proxy_team_id || ''}
                onChange={handleCustomerChange}
                className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg focus:outline-none focus:border-slate-600 text-slate-200"
              >
                <option value="">-- Select a customer --</option>
                {customers.map(customer => (
                  <option key={customer.customer_proxy_team_id} value={customer.customer_proxy_team_id}>
                    {customer.customer_name}
                    {customer.has_offline_grants ? ` (${customer.active_grant_count} grants)` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {selectedCustomer && (
            <>
              <div className="flex border-b border-slate-700 mb-6">
                <button
                  onClick={() => setActiveTab('grants')}
                  className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                    activeTab === 'grants'
                      ? 'border-indigo-500 text-indigo-400'
                      : 'border-transparent text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <Key className="w-4 h-4 inline mr-2" />
                  License Grants
                </button>
                <button
                  onClick={() => setActiveTab('appliances')}
                  className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                    activeTab === 'appliances'
                      ? 'border-indigo-500 text-indigo-400'
                      : 'border-transparent text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <Server className="w-4 h-4 inline mr-2" />
                  Appliances
                </button>
              </div>

              {activeTab === 'grants' && (
                <div className="space-y-6">
                  <GrantIssuanceForm
                    customer={selectedCustomer}
                    onGrantIssued={() => {
                      loadExpiringGrants();
                    }}
                  />
                  <GrantHistory customer={selectedCustomer} />
                </div>
              )}

              {activeTab === 'appliances' && (
                <ApplianceList customer={selectedCustomer} />
              )}
            </>
          )}

          {!selectedCustomer && (
            <div className="text-center py-8 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Select a customer above to manage their offline licenses.</p>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-slate-100">Offline License Management</h1>
          <p className="text-sm text-slate-500 mt-1">
            Issue seat-based licenses for air-gapped environments.{' '}
            <a href="/docs/offline-integration.html" className="text-indigo-400 hover:text-indigo-300">
              Integration guide →
            </a>
            {' · '}
            <a href="/docs/license-manager-admin.html" className="text-indigo-400 hover:text-indigo-300">
              Admin docs →
            </a>
          </p>
        </div>
        <button
          onClick={handleRefresh}
          className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg flex items-center text-sm transition-colors"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {renderContent()}
    </div>
  );
}
